import {
  type Icon as TablerIcon,
  IconEye,
  IconListDetails,
  IconPencil,
  IconPlayerPlay,
  IconPlus,
  IconSend,
  IconTrash,
  IconUpload,
} from '@common/ui/icons'
import { com } from '@eidu/web'
import { PermissionWithId } from '../input/PermissionsInput'
import { findEntityTypeByFieldId } from '../../util/EntityTypeExtensions'
import { groupEntityTypesByKind } from '../../util/entityKindGroup'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import EntitySelector = com.eidu.entity.selector.EntitySelector
import GlobalAction = com.eidu.entity.permission.GlobalAction
import GlobalPermission = com.eidu.entity.permission.GlobalPermission
import EntityAction = com.eidu.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.entity.permission.EntityPathPermission
import Action = com.eidu.entity.permission.Action
import Permission = com.eidu.entity.permission.Permission

export type GlobalRow = { id: string; action: Action; label: string }
export type EntityRow = { id: string; entityTypeId: EntityTypeId; entityType: EntityType; label: string }
export type KindHeaderRow = { id: string; kindKey: string; label: string }
export type EntitiesDisplayRow = { kind: 'entity'; row: EntityRow } | { kind: 'header'; row: KindHeaderRow }

export const buildEntitiesDisplayRows = (rows: readonly EntityRow[]): EntitiesDisplayRow[] => {
  const groups = groupEntityTypesByKind(rows, (row) => row.entityType)
  const out: EntitiesDisplayRow[] = []
  for (const group of groups) {
    out.push({ kind: 'header', row: { id: `kind-${group.kindKey}`, kindKey: group.kindKey, label: group.label } })
    const sorted = group.entries
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    for (const r of sorted) out.push({ kind: 'entity', row: r })
  }
  return out
}

export type ColDef = {
  action: EntityAction
  label: string
  Icon: TablerIcon
  allowsPath: boolean
  // When set, the column only renders cells for rows where this returns true — used for the form-only
  // SubmitForm / ViewFormSubmissions actions. Other rows render a blank cell so the grid stays aligned.
  appliesToRow?: (row: EntityRow) => boolean
}

const isFormRow = (row: EntityRow): boolean => row.entityType.kind.equals(EntityTypeKind.Companion.Form)

export const ENTITY_COLS: readonly ColDef[] = [
  { action: EntityAction.CreateEntity, label: 'Create', Icon: IconPlus, allowsPath: false },
  { action: EntityAction.ReadEntity, label: 'Read', Icon: IconEye, allowsPath: true },
  { action: EntityAction.UpdateEntity, label: 'Update', Icon: IconPencil, allowsPath: false },
  { action: EntityAction.DeleteEntity, label: 'Delete', Icon: IconTrash, allowsPath: false },
  { action: EntityAction.PublishAsset, label: 'Publish', Icon: IconUpload, allowsPath: false },
  { action: EntityAction.UseConfig, label: 'Use', Icon: IconPlayerPlay, allowsPath: false },
  { action: EntityAction.SubmitForm, label: 'Submit', Icon: IconSend, allowsPath: false, appliesToRow: isFormRow },
  {
    action: EntityAction.ViewFormSubmissions,
    label: 'View',
    Icon: IconListDetails,
    allowsPath: false,
    appliesToRow: isFormRow,
  },
]

// EntityActions granted globally (rendered in the Global section, not the per-type matrix). Only
// ShareSchoolLogin remains; SubmitForm / ViewFormSubmissions moved into the form row as scoped columns.
export const GLOBAL_ENTITY_ACTIONS: readonly EntityAction[] = [EntityAction.ShareSchoolLogin]

export const cellKey = (rowId: string, action: EntityAction) => `${rowId}::${action}`

export const anchorTypeIdOf = (permission: Permission, entityTypes: readonly EntityType[]): EntityTypeId | null => {
  if (permission instanceof EntityPathPermission) return permission.path.toType
  if (permission instanceof EntitySelectorPermission) {
    const t = permission.targets
    if (t instanceof EntitySelector.OfType) return t.typeId
    if (t instanceof EntitySelector.Single) return t.typeId
    if (
      t instanceof EntitySelector.FieldValueEquals ||
      t instanceof EntitySelector.FieldValueEqualsUserEntityId ||
      t instanceof EntitySelector.FieldValueEqualsUserFieldValue
    )
      return findEntityTypeByFieldId(entityTypes, t.fieldId)?.id ?? null
  }
  return null
}

export const isUserSelectorAnchored = (userSelector: EntitySelector) =>
  userSelector instanceof EntitySelector.OfType || userSelector instanceof EntitySelector.FieldValueEquals

export const deriveUserEntityTypeId = (
  userSelector: EntitySelector,
  entityTypes: readonly EntityType[]
): EntityTypeId | undefined => {
  if (userSelector instanceof EntitySelector.OfType) return userSelector.typeId
  if (userSelector instanceof EntitySelector.FieldValueEquals)
    return findEntityTypeByFieldId(entityTypes, userSelector.fieldId)?.id
  return undefined
}

export const isAnchorable = (permission: Permission): boolean => {
  if (permission instanceof EntityPathPermission) return true
  if (permission instanceof EntitySelectorPermission) {
    const t = permission.targets
    return (
      t instanceof EntitySelector.OfType ||
      t instanceof EntitySelector.Single ||
      t instanceof EntitySelector.FieldValueEquals ||
      t instanceof EntitySelector.FieldValueEqualsUserEntityId ||
      t instanceof EntitySelector.FieldValueEqualsUserFieldValue
    )
  }
  return false
}

export const isGlobalRowPermission = (permission: Permission, action: Action): boolean => {
  if (action instanceof GlobalAction) return permission instanceof GlobalPermission && permission.action === action
  if (action instanceof EntityAction)
    return (
      permission instanceof EntitySelectorPermission &&
      permission.action === action &&
      EntitySelector.All.equals(permission.targets)
    )
  return false
}

export const isAllRowPermission = (permission: Permission, action: EntityAction): boolean =>
  permission instanceof EntitySelectorPermission &&
  permission.action === action &&
  EntitySelector.All.equals(permission.targets)

export const buildGlobalPermission = (action: Action): Permission =>
  action instanceof GlobalAction
    ? new GlobalPermission(action)
    : new EntitySelectorPermission(action as EntityAction, EntitySelector.All)

export const isLimitedForRow = (permission: Permission, rowEntityTypeId: EntityTypeId): boolean => {
  if (permission instanceof EntityPathPermission) return true
  if (!(permission instanceof EntitySelectorPermission)) return true
  const t = permission.targets
  switch (true) {
    case t instanceof EntitySelector.OfType:
      return !t.typeId.equals(rowEntityTypeId)
    default:
      return true
  }
}

export type CellFlavour = 'oftype' | 'single' | 'filtered' | 'path'

export const flavourOf = (permission: Permission): CellFlavour | null => {
  if (permission instanceof EntityPathPermission) return 'path'
  if (!(permission instanceof EntitySelectorPermission)) return null
  const t = permission.targets
  switch (true) {
    case t instanceof EntitySelector.OfType:
      return 'oftype'
    case t instanceof EntitySelector.Single:
      return 'single'
    case t instanceof EntitySelector.FieldValueEquals:
    case t instanceof EntitySelector.FieldValueEqualsUserEntityId:
    case t instanceof EntitySelector.FieldValueEqualsUserFieldValue:
      return 'filtered'
    default:
      return null
  }
}

export const cellLabel = (matching: readonly PermissionWithId[]): string | null => {
  if (matching.length === 0) return null
  const flavours = new Set<CellFlavour>()
  for (const m of matching) {
    const f = flavourOf(m.permission)
    if (f) flavours.add(f)
  }
  if (flavours.size > 1) return '*complex'
  if (flavours.has('path')) return '*assigned'
  if (flavours.has('filtered')) return '*filtered'
  if (flavours.has('single')) return '*specific'
  return null
}

export type EditorMode = 'all' | 'single' | 'fve' | 'fveUserField' | 'fveUserEntity' | 'path'

export const EDITOR_MODE_OPTIONS: { value: EditorMode; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'single', label: 'Specific entity' },
  { value: 'fve', label: 'Field value equals' },
  { value: 'fveUserField', label: 'Field value equals user field value' },
  { value: 'fveUserEntity', label: 'Field value is user' },
  { value: 'path', label: 'Assignment' },
]

export const editorModeOf = (permission: Permission): EditorMode => {
  if (permission instanceof EntityPathPermission) return 'path'
  if (permission instanceof EntitySelectorPermission) {
    const t = permission.targets
    if (t instanceof EntitySelector.Single) return 'single'
    if (t instanceof EntitySelector.FieldValueEquals) return 'fve'
    if (t instanceof EntitySelector.FieldValueEqualsUserFieldValue) return 'fveUserField'
    if (t instanceof EntitySelector.FieldValueEqualsUserEntityId) return 'fveUserEntity'
  }
  return 'all'
}
